import type { ProfileListing } from "@/components/profile/profile-types";

export const LISTING_GROUPS = [
  { key: "sale", label: "Active for Sale" },
  { key: "sold", label: "Sold" },
  { key: "rentals", label: "Active Rentals" },
  { key: "rented", label: "Rented" },
] as const;
export type ListingGroup = "sale" | "sold" | "rentals" | "rented";

// Exact source PropertyType values. No subtype/price/address inference.
// Boat Dock is intentionally unclassified in these tabs; property pages stay available.
const SALE_TYPES = new Set(["residential", "residential income", "land", "commercial sale", "business opportunity"]);
const LEASE_TYPES = new Set(["residential lease", "commercial lease"]);
export function listingGroup(listing: Pick<ProfileListing, "propertyType" | "status">): ListingGroup | null {
  const type = listing.propertyType?.trim().toLowerCase();
  if (type && SALE_TYPES.has(type)) return listing.status === "closed" ? "sold" : "sale";
  if (type && LEASE_TYPES.has(type)) return listing.status === "closed" ? "rented" : "rentals";
  return null;
}

/** Date-only source facts; reject impossible dates rather than rolling them forward. */
export function listingDateValue(value: string | null): number | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00Z`);
  const time = date.getTime();
  return Number.isFinite(time) && date.toISOString().slice(0, 10) === value ? time : null;
}
export function compareProfileListings(a: ProfileListing, b: ProfileListing): number {
  const dateA = listingDateValue(a.status === "closed" ? a.soldDate : a.listDate);
  const dateB = listingDateValue(b.status === "closed" ? b.soldDate : b.listDate);
  if (dateA !== dateB) {
    if (dateA === null) return 1;
    if (dateB === null) return -1;
    return dateB - dateA;
  }
  return a.mlsListingKey.localeCompare(b.mlsListingKey) || a.id.localeCompare(b.id);
}

/** Preserve the existing active-first, highest-price banner choice independently of tab order. */
export function profileBannerPhoto(listings: ProfileListing[]): string | null {
  const rank = { active: 0, pending: 1, closed: 2 };
  const ordered = [...listings].sort((a, b) => rank[a.status] - rank[b.status]
    || (b.price ?? -Infinity) - (a.price ?? -Infinity)
    || a.mlsListingKey.localeCompare(b.mlsListingKey));
  return ordered[0]?.photoUrl ?? null;
}
